import {
  EmailAuthProvider,
  GoogleAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { adminRequest } from "./adminSiteApi";
import { getExternalAuth } from "lib/firebase";

function getAdminServices() {
  const auth = getExternalAuth();

  if (!auth) {
    throw new Error(
      "Admin login is not configured. Check the Firebase environment variables.",
    );
  }

  return { auth };
}

export async function loadAdminProfile(user) {
  if (!user?.uid) throw new Error("Sign in to access the admin area.");
  const { data } = await adminRequest("/admin/me", { user });
  return {
    ...data,
    uid: data.id,
    email: user.email || "",
    displayName: user.displayName || "",
    active: true,
  };
}

export async function signInAdmin(email, password) {
  const { auth } = getAdminServices();
  const credential = await signInWithEmailAndPassword(
    auth,
    String(email || "").trim(),
    password,
  );

  try {
    const profile = await loadAdminProfile(credential.user);
    return { user: credential.user, profile };
  } catch (error) {
    await signOut(auth);
    throw error;
  }
}

export async function signInAdminWithGoogle() {
  const { auth } = getAdminServices();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });

  const credential = await signInWithPopup(auth, provider);

  try {
    const profile = await loadAdminProfile(credential.user);
    return { user: credential.user, profile };
  } catch (error) {
    await signOut(auth);
    throw error;
  }
}

// Secret changes (Stripe keys, webhook secrets) need a sign-in from the last 5 minutes; the server checks it.
export async function adminSignInAge() {
  const user = getExternalAuth()?.currentUser;
  if (!user) return Infinity;
  const result = await user.getIdTokenResult();
  const at = Date.parse(result.authTime);
  return Number.isFinite(at) ? Date.now() - at : Infinity;
}
export function adminSignInMethod() {
  const user = getExternalAuth()?.currentUser;
  return user?.providerData?.some((p) => p.providerId === "google.com") ? "google" : "password";
}
// The person confirms their own sign-in; the password goes only to Firebase, never to the website server.
export async function confirmAdminSignIn(password) {
  const { auth } = getAdminServices();
  const user = auth.currentUser;
  if (!user) throw new Error("Sign in to access the admin area.");
  if (adminSignInMethod() === "google") {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account", login_hint: user.email || "" });
    await reauthenticateWithPopup(user, provider);
  } else {
    await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
  }
  await user.getIdToken(true);
}

export async function signOutAdmin() {
  const { auth } = getAdminServices();
  await signOut(auth);
}

export function watchAdminAuth(onChange) {
  const { auth } = getAdminServices();
  let live = true, sequence = 0, verifiedUser = null;
  // Auth changes and every recheck share a generation. A result for a previous
  // identity (including logout then login) cannot restore its permissions.
  const verify = async (user, block = false) => {
    const current = ++sequence;
    if (!live) return;
    if (!user) {
      verifiedUser = null;
      onChange({ status: "signed_out", user: null, profile: null, error: null });
      return;
    }
    if (block || verifiedUser !== user) {
      verifiedUser = null;
      onChange({ status: "checking", user, profile: null, error: null });
    }
    const isCurrent = () => live && current === sequence && auth.currentUser === user;
    try {
      const profile = await loadAdminProfile(user);
      if (isCurrent()) {
        verifiedUser = user;
        onChange({ status: "authorized", user, profile, error: null });
      }
    } catch (error) {
      if (isCurrent()) {
        verifiedUser = null;
        onChange({ status: "unauthorized", user: null, profile: null, error });
      }
    }
  };
  const stop = onAuthStateChanged(auth, user => verify(user, true), error => {
    sequence++;
    verifiedUser = null;
    if (live) onChange({ status: "error", user: null, profile: null, error });
  });
  const refresh = () => verify(auth.currentUser);
  const recheck = () => verify(auth.currentUser, true);
  const timer = setInterval(refresh, 30000);
  window.addEventListener("focus", refresh);
  window.addEventListener("magellan-admin-recheck", recheck);
  return () => {
    live = false;
    sequence++;
    stop();
    clearInterval(timer);
    window.removeEventListener("focus", refresh);
    window.removeEventListener("magellan-admin-recheck", recheck);
  };
}
